import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material'
import { ReactElement, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import styled, { css } from 'styled-components'
import { breakpoints } from '../../theme/breakpoints'

interface Props<T> {
  items: Array<T>
  renderItem: (item: T, idx: number) => ReactElement
  itemsPerView: number
  loading?: boolean
}

function GenericCarousel<T>({
  items,
  renderItem,
  itemsPerView,
  loading = false,
}: Props<T>): ReactElement {
  const [currentIndex, setCurrentIndex] = useState(0)

  const handleUp = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleDown = () => {
    if (currentIndex < items.length - itemsPerView) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  const visibleItems = items.slice(currentIndex, currentIndex + itemsPerView)

  return (
    <Wrapper>
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <CardWrapper>
          <ArrowLeft disabled={currentIndex === 0} onClick={handleUp} />
          <ArrowUp disabled={currentIndex === 0} onClick={handleUp} />
          {visibleItems.map((item) => renderItem(item, currentIndex))}
          <ArrowDown
            disabled={currentIndex >= items.length - itemsPerView}
            onClick={handleDown}
          />
          <ArrowRight
            disabled={currentIndex >= items.length - itemsPerView}
            onClick={handleDown}
          />
        </CardWrapper>
      )}
    </Wrapper>
  )
}

interface ArrowProps {
  disabled: boolean
}

// Styled components
const Wrapper = styled.div`
  display: flex;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 100%;
  }
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    width: 100%;
    flex-direction: row;
    justify-content: center;
  }
`

const BaseArrow = css<ArrowProps>`
  color: #adadaa;
  display: block;
  cursor: pointer;
  font-size: 50px;
  visibility: ${(props) => (props.disabled ? 'hidden' : 'visible')};
`

const ArrowUp = styled(KeyboardArrowUp)<ArrowProps>`
  ${BaseArrow};
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const ArrowDown = styled(KeyboardArrowDown)<ArrowProps>`
  ${BaseArrow};
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const ArrowLeft = styled(KeyboardArrowLeft)<ArrowProps>`
  ${BaseArrow}
  display: none;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
  }
`

const ArrowRight = styled(KeyboardArrowRight)<ArrowProps>`
  ${BaseArrow}
  display: none;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
  }
`

export default GenericCarousel
